import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import ProviderConfigForm from './ProviderConfigForm'

export default function ProviderConfigsTab() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [deletingConfig, setDeletingConfig] = useState(null)
  const [viewingConfig, setViewingConfig] = useState(null)

  const { data: configs, loading, error, refetch } = useSupabaseQuery('EmailProviderConfigs', {
    order: { column: 'Name', ascending: true }
  })

  const { data: providers } = useSupabaseQuery('EmailProvider', {
    order: { column: 'EmailProviderName', ascending: true }
  })

  const { insert, update, remove, loading: mutating } = useSupabaseMutation('EmailProviderConfigs')

  const handleCreate = async (formData) => {
    const { error } = await insert(formData)
    if (!error) {
      setShowCreateModal(false)
      refetch()
    }
  }

  const handleUpdate = async (formData) => {
    const { error } = await update(editingConfig.EmailProviderConfigId, formData, 'EmailProviderConfigId')
    if (!error) {
      setEditingConfig(null)
      refetch()
    }
  }

  const handleDelete = async () => {
    const { error } = await remove(deletingConfig.EmailProviderConfigId, 'EmailProviderConfigId')
    if (!error) {
      setDeletingConfig(null)
      refetch()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Provider Configurations</h2>
          <p className="text-gray-600 dark:text-gray-400">Configure SendGrid and other email providers</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Configuration</span>
        </button>
      </div>

      {/* Configs List */}
      <div className="space-y-4">
        {configs?.map((config) => {
          const provider = providers?.find(p => p.EmailProviderId === config.EmailProviderId)
          const jsonConfig = config.JsonConfig || {}
          
          return (
            <div
              key={config.EmailProviderConfigId}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {config.Name}
                    </h3>
                    {provider && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs font-semibold rounded">
                        {provider.EmailProviderName}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">API Key Status</div>
                      <div className="text-sm">
                        {jsonConfig.ApiKey ? (
                          <span className="text-green-600 dark:text-green-400 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Configured
                          </span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">Not configured</span>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Webhook URL</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {jsonConfig.CallbackUrl || <span className="italic">Not set</span>}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Config ID</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {config.EmailProviderConfigId}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => setViewingConfig(config)}
                    className="p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded transition"
                    title="View Details"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setEditingConfig(config)}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingConfig(config)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {(!configs || configs.length === 0) && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No provider configurations</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Set up SendGrid or other email providers.</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="Create Provider Configuration" onClose={() => setShowCreateModal(false)}>
          <ProviderConfigForm
            providers={providers}
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {editingConfig && (
        <Modal title="Edit Provider Configuration" onClose={() => setEditingConfig(null)}>
          <ProviderConfigForm
            config={editingConfig}
            providers={providers}
            onSubmit={handleUpdate}
            onCancel={() => setEditingConfig(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {/* View Modal */}
      {viewingConfig && (
        <Modal title={`Configuration: ${viewingConfig.Name}`} onClose={() => setViewingConfig(null)} size="lg">
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">JSON Configuration</h4>
              <pre className="text-xs text-gray-800 dark:text-gray-200 overflow-x-auto">
                {JSON.stringify(viewingConfig.JsonConfig, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deletingConfig && (
        <Modal title="Delete Provider Configuration" onClose={() => setDeletingConfig(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingConfig.Name}</strong>?
              This will affect all accounts using this configuration.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingConfig(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
